import { Link } from 'react-router-dom';
import type { Contractor, TaskItem, ProjectEstimateView, CoiStatus } from '@bpd/shared';
import { computeCoiStatus } from '@bpd/shared';

const COI_BADGE_CLS: Record<CoiStatus, string> = {
  green: 'bg-green-100 text-green-800',
  yellow: 'bg-amber-100 text-amber-800',
  red: 'bg-red-100 text-red-800',
  none: 'bg-gray-100 text-gray-500',
};

function Sep() {
  return <span className="text-gray-300">&middot;</span>;
}

interface ProjectContractorsSectionProps {
  contractors: Contractor[];
  /** Top-level tasks only, matching the same v1 simplification as deriveProjectGates. */
  tasks: TaskItem[];
  estimateViews: ProjectEstimateView[];
}

/** Every contractor with a task or an estimate on this project, each with its own CoI status —
 * so a stale/missing certificate is visible right where the project's work is being planned,
 * not only by hunting through the Contractors list. */
export function ProjectContractorsSection({ contractors, tasks, estimateViews }: ProjectContractorsSectionProps) {
  const contractorIds = new Set<string>();
  for (const t of tasks) if (t.contractorId) contractorIds.add(t.contractorId);
  for (const e of estimateViews) if (e.contractorId) contractorIds.add(e.contractorId);

  const rows = contractors
    .filter((c) => contractorIds.has(c.id))
    .map((c) => ({
      contractor: c,
      taskCount: tasks.filter((t) => t.contractorId === c.id).length,
      estimateCount: estimateViews.filter((e) => e.contractorId === c.id).length,
      coi: computeCoiStatus(c.coiExpirationDate),
    }))
    .sort((a, b) => a.contractor.name.localeCompare(b.contractor.name));

  return (
    <section className="mt-10">
      <h2 className="font-serif text-xl font-semibold text-phoenix-blue-900">Contractors</h2>
      {rows.length === 0 ? (
        <p className="mt-3 text-sm text-gray-400">No contractors recorded on this project's tasks or estimates yet.</p>
      ) : (
        <div className="mt-3 space-y-3">
          {rows.map(({ contractor, taskCount, estimateCount, coi }) => (
            <Link
              key={contractor.id}
              to={`/admin/contractors/${contractor.id}`}
              className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-gray-200 bg-white p-3 text-sm shadow-sm hover:border-phoenix-blue-300 hover:shadow-md"
            >
              <span className="font-semibold text-gray-900">{contractor.name}</span>
              {contractor.trade && (
                <>
                  <Sep />
                  <span className="text-gray-500">{contractor.trade}</span>
                </>
              )}
              <Sep />
              <span className="text-gray-500">
                {[
                  taskCount > 0 ? `${taskCount} task${taskCount === 1 ? '' : 's'}` : null,
                  estimateCount > 0 ? `${estimateCount} estimate${estimateCount === 1 ? '' : 's'}` : null,
                ]
                  .filter(Boolean)
                  .join(', ')}
              </span>
              <Sep />
              <span
                title={coi.reason}
                className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${COI_BADGE_CLS[coi.status]}`}
              >
                CoI: {coi.status === 'none' ? 'missing' : coi.reason}
              </span>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}
